/*
 * ImageZoom Web Component
 *
 * This custom element provides an interactive image zoom and panning feature.
 * It allows users to click or tap an image to toggle a zoomed view, where the image is
 * displayed as a background with a configurable zoom level (default: 2x).
 * The zoomed image pans smoothly based on mouse or touch movement, ensuring
 * the image edges align with the container edges without overflow.
 *
 * Key Features:
 * - Supports any image size and screen resolution.
 * - Configurable zoom level via the 'zoom' attribute (e.g., zoom="1.5" for 150% zoom).
 * - Smooth opacity transition for the image when toggling zoom state.
 * - Optimized event handling with debouncing for performance during rapid mouse/touch movements.
 * - Prevents overflow with a buffer zone for precise panning.
 * - Handles dynamic image loading for Shopify's img_url filter.
 * - Stable single-tap detection (<400ms, <5px movement) for zoom mode toggle in modal slider.
 * - Prevents modal closing on mobile by blocking click and pointerup events during zoom toggle.
 * - Allows touch events to propagate to SliderComponent when not in panning mode for slide navigation.
 * - Applies larger zoom level on mobile devices for better visibility.
 *
 * Usage:
 * <image-zoom src="image.jpg" alt="Description" zoom="2"></image-zoom>
 */

class ImageZoom extends HTMLElement {
  constructor() {
    super();
    // Initialize constants and state
    this.buffer = 10; // Buffer zone for smoother panning near edges
    this.isPanning = true; // Always start zoomed in
    this.lastMoveTime = 0; // Timestamp for debouncing mouse/touch events
    this.moveTimeout = null; // Timeout for debounced panning
    this.touchStartTime = 0; // Timestamp for touchstart to detect single tap
    this.isPendingTap = false; // Tracks if a tap is being evaluated
    this.touchMoved = false; // Tracks if touchmove occurred to prevent accidental taps
    this.touchStartX = 0; // X coordinate of touchstart for movement detection
    this.touchStartY = 0; // Y coordinate of touchstart for movement detection
    this.touchStartMouseX = 0; // New: Store touchstart mouseX
    this.touchStartMouseY = 0; // New: Store touchstart mouseY

    // Create container element
    this.container = document.createElement('div');
    this.container.className = 'image-container';

    // Create image element
    this.img = document.createElement('img');
    this.img.src = this.getAttribute('src') || ''; // Set image source from attribute
    this.img.alt = this.getAttribute('alt') || 'Image'; // Set alt text from attribute
    this.img.onload = () => {
      this.initializeZoom();
      // Start zoomed immediately
      this.container.classList?.add('panning');
      this.container.style.backgroundImage = `url(${this.img.src})`;
      const rect = this.container.getBoundingClientRect();
      this.panImage({
        mouseX: rect.width / 2,
        mouseY: rect.height / 2
      });
    };
    this.container.appendChild(this.img);

    // Attach event listeners for interaction
    this.container.addEventListener('click', (e) => this.handleEvent(e));
    this.container.addEventListener('touchstart', (e) => this.handleEvent(e), {passive: true});
    this.container.addEventListener('touchend', (e) => this.handleEvent(e));
    this.container.addEventListener('mousemove', (e) => this.handleEvent(e));
    this.container.addEventListener('touchmove', (e) => this.handleEvent(e), {passive: true});
    // Add click event listener to prevent modal closing
    this.container.addEventListener('click', (e) => {
      if (this.isPendingTap || this.isPanning) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    // Append container to the component
    this.appendChild(this.container);
  }

  // Initialize zoom level from the 'zoom' attribute
  initializeZoom() {
    let zoom = parseFloat(this.getAttribute('zoom')) || 2; // Default to 2x if not specified
    // Increase zoom level on mobile devices for better visibility
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      zoom *= 1.4; // Increase zoom by 1.4x on mobile (e.g., 1.5x becomes ~2.1x)
    }
    if (zoom > 0) { // Validate zoom value
      this.container.style.setProperty('--zoom', `${zoom * 100}%`);
    }
  }

  // Define attributes to observe for changes
  static get observedAttributes() {
    return ['zoom', 'src', 'alt'];
  }

  // Handle attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'zoom') {
      const zoom = parseFloat(newValue);
      if (zoom > 0) {
        this.container.style.setProperty('--zoom', `${zoom * 100}%`);
      }
    } else if (name === 'src') {
      this.img.src = newValue;
    } else if (name === 'alt') {
      this.img.alt = newValue;
    }
  }

  /**
 * Calculate mouse/touch coordinates relative to the container.
 * @param {MouseEvent | TouchEvent} event - The event object containing mouse or touch data.
 * @returns {{ mouseX: number, mouseY: number }} An object containing the x and y coordinates relative to the container.
 */
  getMouseCoordinates(event) {
    const containerRect = this.container.getBoundingClientRect();
    const clientX = event.clientX || (event.touches && event.touches[0].clientX);
    const clientY = event.clientY || (event.touches && event.touches[0].clientY);
    const mouseX = clientX - containerRect.left;
    const mouseY = clientY - containerRect.top;
    return { mouseX, mouseY };
  }

  // Centralized event handler for all interactions
  handleEvent(event) {
    const button = event.target.closest('[js-prevent-zoom="true"]');
    if (button || event.target.hasAttribute('js-prevent-zoom')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    switch (event.type) {
      case 'touchstart':
        this.handleTouchstartEvent(event);
        break;

      case 'touchend':
        this.handleTouchendEvent(event);
        break;

      case 'mousemove':
        this.handleMousemoveEvent(event);
        break;

      case 'touchmove':
        this.handleTouchmoveEvent(event);
        break;

      default:
        break;
    }
  }

  // Handle touchstart events - only track coordinates for panning
  handleTouchstartEvent(event) {
    if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      const { mouseX, mouseY } = this.getMouseCoordinates(event);
      this.touchStartMouseX = mouseX;
      this.touchStartMouseY = mouseY;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // Handle touchend events - disabled toggle to keep zoom always on
  handleTouchendEvent(event) {
    if (this.isPendingTap) {
      event.preventDefault();
      event.stopPropagation();
      this.isPendingTap = false;
      this.touchMoved = false;
    }
  }

  // Handle mousemove events
  handleMousemoveEvent(event) {
    if (this.isPanning) {
      const now = performance.now();
      if (now - this.lastMoveTime > 16) {
        this.lastMoveTime = now;
        this.panImage(event);
      } else {
        clearTimeout(this.moveTimeout);
        this.moveTimeout = setTimeout(() => this.panImage(event), 16);
      }
    }
  }

  // Handle touchmove events - always pan, no toggle detection needed
  handleTouchmoveEvent(event) {
    event.preventDefault();
    event.stopPropagation();

    const now = performance.now();
    if (now - this.lastMoveTime > 16) {
      this.lastMoveTime = now;
      this.panImage(event);
    } else {
      clearTimeout(this.moveTimeout);
      this.moveTimeout = setTimeout(() => this.panImage(event), 16);
    }
  }

  // Toggle between zoomed/panning and normal states
  togglePanning(event) {
    this.isPanning = !this.isPanning;
    if (this.isPanning) {
      this.container.classList?.add('panning');
      this.container.style.backgroundImage = `url(${this.img.src})`;
      // Use provided coordinates or default to center
      const mouseX = typeof event.mouseX === 'number' ? event.mouseX : this.container.getBoundingClientRect().width / 2;
      const mouseY = typeof event.mouseY === 'number' ? event.mouseY : this.container.getBoundingClientRect().height / 2;
      this.panImage({ mouseX, mouseY });
      return;
    }
      this.container.classList?.remove('panning');
      this.container.style.backgroundImage = 'none';
  }

  // Update background position based on mouse/touch coordinates
  panImage(event) {
    let mouseX = event.mouseX;
    let mouseY = event.mouseY;
    if (typeof mouseX !== 'number' || typeof mouseY !== 'number') {
      const coords = this.getMouseCoordinates(event);
      mouseX = coords.mouseX;
      mouseY = coords.mouseY;
    }

    const containerRect = this.container.getBoundingClientRect();
    const xPercentage = Math.max(0, Math.min(1, (mouseX - this.buffer) / (containerRect.width - this.buffer * 2))) * 100;
    const yPercentage = Math.max(0, Math.min(1, (mouseY - this.buffer) / (containerRect.height - this.buffer * 2))) * 100;

    this.container.style.backgroundPosition = `${xPercentage}% ${yPercentage}%`;
  }
}

customElements.define('image-zoom', ImageZoom);